import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { CONSUMABLES, PERMANENT_UPGRADES } from "@/hooks/useShop";
import { X, Star, ShoppingBag, Sparkles, Check, CreditCard, Loader2, Store, ArrowRight, Coins, Crown, Heart, Zap, Gift } from "lucide-react";
import { GrandExchange } from "./GrandExchange";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const TOKEN_KEY = 'fruitCrush_token';

// Membership benefits for display
const MEMBERSHIP_BENEFITS = [
  { emoji: "⚡", name: "Premium Battle Pass", desc: "Full access to all rewards" },
  { emoji: "❤️", name: "+500 Max Health", desc: "Start with more hearts" },
  { emoji: "✨", name: "1.2x XP Boost", desc: "20% more XP always" },
  { emoji: "🪙", name: "2x Coins", desc: "Double coins from levels" },
  { emoji: "⭐", name: "500 Stars Monthly", desc: "Free stars each month" },
  { emoji: "💰", name: "10% Shop Discount", desc: "Save on purchases" },
  { emoji: "💎", name: "Crystal Paradise", desc: "50 exclusive levels" },
  { emoji: "👑", name: "Royal Citadel", desc: "VIP 12x12 world" },
  { emoji: "🎁", name: "Daily Rewards", desc: "Premium daily bonuses" },
  { emoji: "🏆", name: "VIP Items", desc: "Exclusive power-ups" },
];

// Star packages for purchase
const STAR_PACKAGES = [
  { id: "stars_100", stars: 100, price: 199, display_price: "€1,99", popular: false },
  { id: "stars_250", stars: 250, price: 449, display_price: "€4,49", popular: true },
  { id: "stars_500", stars: 500, price: 799, display_price: "€7,99", popular: false },
  { id: "stars_1000", stars: 1000, price: 1499, display_price: "€14,99", popular: false },
];

export function ShopModal({ 
  isOpen, 
  onClose, 
  totalStars,
  totalCoins,
  inventory, 
  equipment,
  permanentUpgrades,
  onBuyConsumable,
  onBuyPermanentUpgrade,
  onAddStars,
  onCoinsChange,
  initialTab = 'consumables'
}) {
  const [activeTab, setActiveTab] = useState(initialTab);
  const [buyingItem, setBuyingItem] = useState(null);
  const [purchasingPackage, setPurchasingPackage] = useState(null);
  const [exchangeStars, setExchangeStars] = useState(1);
  const [isExchanging, setIsExchanging] = useState(false);
  
  // Membership state
  const [membershipStatus, setMembershipStatus] = useState(null);
  const [purchasingMembership, setPurchasingMembership] = useState(false);
  
  // Fruit exchange state
  const [collectedFruits, setCollectedFruits] = useState([]);
  const [isLoadingFruits, setIsLoadingFruits] = useState(false);
  const [isExchangingFruits, setIsExchangingFruits] = useState(false);

  // Exchange rate: 1 star = 100 coins
  const EXCHANGE_RATE = 100;
  
  // Fetch collected fruits when exchange tab is active
  useEffect(() => {
    if (isOpen && activeTab === 'exchange') {
      fetchCollectedFruits();
    }
  }, [isOpen, activeTab]);
  
  // Fetch membership status when VIP tab is active
  useEffect(() => {
    if (isOpen && activeTab === 'vip') {
      fetchMembershipStatus();
    }
  }, [isOpen, activeTab]);

  const fetchMembershipStatus = async () => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      if (!token) return;
      
      const payload = JSON.parse(atob(token.split('.')[1]));
      const userId = payload.user_id;
      
      const response = await fetch(`${API_URL}/api/stripe/membership-status/${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMembershipStatus(data);
      }
    } catch (error) {
      console.error('Failed to fetch membership status:', error);
    }
  };

  const handlePurchaseMembership = async () => {
    setPurchasingMembership(true);
    
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      let userId = null;
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          userId = payload.user_id;
        } catch (e) {
          console.warn('Could not decode token');
        }
      }
      
      if (!userId) {
        toast.error('Please log in to purchase membership');
        setPurchasingMembership(false);
        return;
      }
      
      const response = await fetch(`${API_URL}/api/stripe/create-membership-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          origin_url: window.location.origin + window.location.pathname,
          user_id: userId,
        }),
      });
      
      const data = await response.json();
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        toast.error('Could not start checkout');
      }
    } catch (error) {
      console.error('Membership checkout failed:', error);
      toast.error('Checkout failed. Please try again.');
    } finally {
      setPurchasingMembership(false);
    }
  };

  const fetchCollectedFruits = async () => {
    setIsLoadingFruits(true);
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const response = await fetch(`${API_URL}/api/exchange/fruits`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCollectedFruits(data.fruits || []);
      }
    } catch (error) {
      console.error('Failed to fetch fruits:', error);
    }
    setIsLoadingFruits(false);
  };

  // Exchange all fruits for coins
  const handleExchangeAllFruits = async () => {
    if (collectedFruits.length === 0) {
      toast.error('No fruits to exchange');
      return;
    }

    setIsExchangingFruits(true);
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const response = await fetch(`${API_URL}/api/exchange/fruits-to-coins/all`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (response.ok) {
        toast.success(`Exchanged ${data.total_fruits_spent} fruits for ${data.coins_received} 🪙!`);
        onCoinsChange?.();
        setCollectedFruits([]);
      } else {
        toast.error(data.detail || 'Exchange failed');
      }
    } catch (error) {
      toast.error('Exchange failed');
    }
    setIsExchangingFruits(false);
  };

  // Exchange single fruit type
  const handleExchangeFruit = async (fruitId, quantity) => {
    setIsExchangingFruits(true);
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const response = await fetch(`${API_URL}/api/exchange/fruits-to-coins`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ fruit_id: fruitId, quantity })
      });

      const data = await response.json();
      if (response.ok) {
        toast.success(`Exchanged ${quantity} ${data.fruit_emoji} for ${data.coins_received} 🪙!`);
        onCoinsChange?.();
        fetchCollectedFruits();
      } else {
        toast.error(data.detail || 'Exchange failed');
      }
    } catch (error) {
      toast.error('Exchange failed');
    }
    setIsExchangingFruits(false);
  };

  // Update active tab when initialTab changes (when modal opens)
  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
    }
  }, [isOpen, initialTab]);

  // Exchange stars for coins
  const handleExchangeStars = async () => {
    if (exchangeStars < 1 || exchangeStars > totalStars) {
      toast.error('Invalid amount');
      return;
    }

    setIsExchanging(true);
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      const response = await fetch(`${API_URL}/api/exchange/stars-to-coins`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ stars: exchangeStars })
      });

      const data = await response.json();
      if (response.ok) {
        toast.success(`Exchanged ${exchangeStars} ⭐ for ${exchangeStars * EXCHANGE_RATE} 🪙!`);
        onCoinsChange?.();  // Refresh data
        setExchangeStars(1);
      } else {
        toast.error(data.detail || 'Exchange failed');
      }
    } catch (error) {
      toast.error('Exchange failed');
    }
    setIsExchanging(false);
  };

  // Check for successful payment on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    const starsParam = urlParams.get('stars');
    
    if (sessionId && starsParam) {
      // Verify the payment and add stars
      verifyPayment(sessionId, parseInt(starsParam, 10));
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const verifyPayment = async (sessionId, stars) => {
    try {
      const response = await fetch(`${API_URL}/api/stripe/verify-session/${sessionId}`);
      const data = await response.json();
      
      if (data.success && data.stars > 0 && onAddStars) {
        onAddStars(data.stars);
        alert(`🎉 Betaling gelukt! ${data.stars} sterren zijn toegevoegd aan je account!`);
      }
    } catch (error) {
      console.error('Payment verification failed:', error);
    }
  };

  const handlePurchaseStars = async (packageId) => {
    setPurchasingPackage(packageId);
    
    try {
      // Get user ID from token
      const token = localStorage.getItem(TOKEN_KEY);
      let userId = null;
      if (token) {
        try {
          const payload = JSON.parse(atob(token.split('.')[1]));
          userId = payload.user_id;
        } catch (e) {
          console.warn('Could not decode token for user_id');
        }
      }
      
      const response = await fetch(`${API_URL}/api/stripe/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          package_id: packageId,
          origin_url: window.location.origin + window.location.pathname,
          user_id: userId,
        }),
      });
      
      const data = await response.json();
      
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        alert('Er ging iets mis. Probeer het opnieuw.');
      }
    } catch (error) {
      console.error('Checkout failed:', error);
      alert('Betaling kon niet worden gestart. Probeer het opnieuw.');
    } finally {
      setPurchasingPackage(null);
    }
  };

  if (!isOpen) return null;

  const handleBuy = async (itemId, isPermanent) => {
    setBuyingItem(itemId);
    const success = isPermanent ? onBuyPermanentUpgrade(itemId) : onBuyConsumable(itemId);
    setTimeout(() => setBuyingItem(null), 300);
    return success;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
      
      <Card 
        className="relative w-full max-w-md max-h-[85vh] overflow-hidden bg-gradient-to-b from-amber-50 to-orange-50 border-4 border-amber-400 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="shop-modal"
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 p-4 text-white">
          <button 
            onClick={onClose}
            className="absolute top-3 right-3 p-1 rounded-full hover:bg-white/20 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <ShoppingBag className="w-7 h-7" />
            </div>
            <div>
              <h2 className="font-display text-2xl font-bold">Shop</h2>
              <div className="flex items-center gap-1">
                <Star className="w-4 h-4 fill-yellow-300 text-yellow-300" />
                <span className="font-bold">{totalStars}</span>
                <span className="text-white/80 text-sm">stars</span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-amber-200">
          <button
            onClick={() => setActiveTab('buy')}
            className={`flex-1 py-3 px-2 font-display font-semibold text-xs transition-colors ${
              activeTab === 'buy' 
                ? 'bg-green-100 text-green-800 border-b-2 border-green-500' 
                : 'text-amber-600 hover:bg-amber-50'
            }`}
          >
            💰 Buy
          </button>
          <button
            onClick={() => setActiveTab('consumables')}
            className={`flex-1 py-3 px-2 font-display font-semibold text-xs transition-colors ${
              activeTab === 'consumables' 
                ? 'bg-amber-100 text-amber-800 border-b-2 border-amber-500' 
                : 'text-amber-600 hover:bg-amber-50'
            }`}
          >
            🎁 Items
          </button>
          <button
            onClick={() => setActiveTab('permanent')}
            className={`flex-1 py-3 px-2 font-display font-semibold text-xs transition-colors ${
              activeTab === 'permanent' 
                ? 'bg-amber-100 text-amber-800 border-b-2 border-amber-500' 
                : 'text-amber-600 hover:bg-amber-50'
            }`}
          >
            💎 Upgrades
          </button>
          <button
            onClick={() => setActiveTab('exchange')}
            className={`flex-1 py-3 px-2 font-display font-semibold text-xs transition-colors ${
              activeTab === 'exchange' 
                ? 'bg-purple-100 text-purple-800 border-b-2 border-purple-500' 
                : 'text-amber-600 hover:bg-amber-50'
            }`}
          >
            🏛️ Exchange
          </button>
          <button
            onClick={() => setActiveTab('vip')}
            className={`flex-1 py-3 px-2 font-display font-semibold text-xs transition-colors ${
              activeTab === 'vip' 
                ? 'bg-gradient-to-r from-amber-100 to-orange-100 text-amber-800 border-b-2 border-amber-500' 
                : 'text-amber-600 hover:bg-amber-50'
            }`}
            data-testid="shop-vip-tab"
          >
            👑 VIP
          </button>
        </div>

        {/* Content */}
        <div className="p-4 overflow-y-auto max-h-[50vh]">
          {/* VIP Membership Tab */}
          {activeTab === 'vip' ? (
            <div className="space-y-4">
              {/* Current Status Banner */}
              {membershipStatus?.is_premium_member ? (
                <div className="p-4 rounded-xl bg-gradient-to-r from-amber-400 via-orange-400 to-amber-500 text-white">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                      <Crown className="w-7 h-7" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg flex items-center gap-2">
                        VIP Member <Check className="w-5 h-5" />
                      </h3>
                      <p className="text-white/90 text-sm">
                        Expires: {new Date(membershipStatus.membership_expires).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="p-4 rounded-xl bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 border-2 border-amber-300">
                  <div className="text-center">
                    <div className="w-16 h-16 mx-auto mb-3 bg-gradient-to-br from-amber-400 to-orange-500 rounded-2xl flex items-center justify-center shadow-lg">
                      <Crown className="w-9 h-9 text-white" />
                    </div>
                    <h3 className="font-bold text-xl text-amber-800">Premium Membership</h3>
                    <p className="text-amber-600 text-sm">Unlock exclusive benefits!</p>
                  </div>
                </div>
              )}
              
              {/* Benefits Grid */}
              <div className="space-y-2">
                <h4 className="font-bold text-amber-800 text-sm flex items-center gap-2">
                  <Sparkles className="w-4 h-4" /> Membership Benefits
                </h4>
                <div className="grid grid-cols-2 gap-2">
                  {MEMBERSHIP_BENEFITS.map((benefit, i) => (
                    <div 
                      key={i}
                      className={`p-2 rounded-lg border transition-all ${
                        membershipStatus?.is_premium_member 
                          ? 'bg-green-50 border-green-300' 
                          : 'bg-white border-amber-200'
                      }`}
                    >
                      <div className="flex items-start gap-2">
                        <span className="text-lg">{benefit.emoji}</span>
                        <div>
                          <p className={`font-bold text-xs ${
                            membershipStatus?.is_premium_member ? 'text-green-800' : 'text-amber-800'
                          }`}>{benefit.name}</p>
                          <p className="text-[10px] text-gray-500">{benefit.desc}</p>
                        </div>
                        {membershipStatus?.is_premium_member && (
                          <Check className="w-3 h-3 text-green-500 ml-auto" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Price and Purchase */}
              {!membershipStatus?.is_premium_member && (
                <div className="p-4 rounded-xl bg-gradient-to-r from-amber-500 via-orange-500 to-amber-600 text-white">
                  <div className="text-center mb-3">
                    <div className="flex items-center justify-center gap-2">
                      <span className="text-3xl font-bold">€11,99</span>
                      <span className="text-white/80">/month</span>
                    </div>
                    <p className="text-white/70 text-sm">Cancel anytime</p>
                  </div>
                  
                  <Button
                    onClick={handlePurchaseMembership}
                    disabled={purchasingMembership}
                    className="w-full h-12 bg-white text-amber-600 hover:bg-white/90 font-bold text-lg"
                    data-testid="purchase-membership-shop-btn"
                  >
                    {purchasingMembership ? (
                      <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Processing...</>
                    ) : (
                      <><CreditCard className="w-5 h-5 mr-2" /> Get VIP Now</>
                    )}
                  </Button>
                  
                  <p className="text-center text-white/60 text-xs mt-2">
                    Secure payment via Stripe
                  </p>
                </div>
              )}
              
              {/* Active Member Info */}
              {membershipStatus?.is_premium_member && (
                <div className="p-3 rounded-lg bg-green-50 border border-green-200 text-center">
                  <p className="text-green-700 text-sm">
                    🎉 You're enjoying all VIP benefits! Thank you for your support!
                  </p>
                </div>
              )}
            </div>
          ) : activeTab === 'exchange' ? (
            <div className="space-y-4">
              {/* Stars to Coins Converter */}
              <div className="p-4 rounded-xl bg-gradient-to-br from-yellow-50 via-amber-50 to-orange-50 border-2 border-yellow-300">
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-yellow-400 to-amber-500 flex items-center justify-center">
                    <Star className="w-5 h-5 text-white fill-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-amber-800">Stars → Coins</h3>
                    <p className="text-xs text-amber-600">1 ⭐ = {EXCHANGE_RATE} 🪙</p>
                  </div>
                </div>
                
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex-1">
                    <label className="text-xs text-amber-700 mb-1 block">Stars to exchange</label>
                    <Input
                      type="number"
                      min={1}
                      max={totalStars}
                      value={exchangeStars}
                      onChange={(e) => setExchangeStars(Math.max(1, Math.min(totalStars, parseInt(e.target.value) || 1)))}
                      className="h-10 text-center font-bold text-lg border-amber-300 focus:border-amber-500"
                    />
                  </div>
                  <ArrowRight className="w-6 h-6 text-amber-500" />
                  <div className="flex-1">
                    <label className="text-xs text-amber-700 mb-1 block">You get</label>
                    <div className="h-10 flex items-center justify-center bg-gradient-to-r from-amber-100 to-yellow-100 rounded-md border border-amber-300">
                      <span className="font-bold text-lg text-amber-700">🪙 {(exchangeStars * EXCHANGE_RATE).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center justify-between text-xs text-amber-600 mb-3">
                  <span>Your stars: ⭐ {totalStars}</span>
                  <button 
                    onClick={() => setExchangeStars(totalStars)}
                    className="text-amber-700 hover:text-amber-900 underline"
                  >
                    Exchange all
                  </button>
                </div>
                
                <Button
                  onClick={handleExchangeStars}
                  disabled={isExchanging || exchangeStars < 1 || exchangeStars > totalStars}
                  className="w-full bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600 text-white font-bold"
                >
                  {isExchanging ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Exchanging...</>
                  ) : (
                    <>Exchange {exchangeStars} ⭐ for {(exchangeStars * EXCHANGE_RATE).toLocaleString()} 🪙</>
                  )}
                </Button>
              </div>
              
              {/* Fruits to Coins Converter */}
              <div className="p-4 rounded-xl bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 border-2 border-green-300">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center">
                      <span className="text-lg">🍎</span>
                    </div>
                    <div>
                      <h3 className="font-bold text-green-800">Fruits → Coins</h3>
                      <p className="text-xs text-green-600">1 fruit = 1 🪙</p>
                    </div>
                  </div>
                  {collectedFruits.length > 0 && (
                    <Button
                      size="sm"
                      onClick={handleExchangeAllFruits}
                      disabled={isExchangingFruits}
                      className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white text-xs"
                    >
                      {isExchangingFruits ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <>Sell All ({collectedFruits.reduce((sum, f) => sum + f.quantity, 0)} 🪙)</>
                      )}
                    </Button>
                  )}
                </div>
                
                {isLoadingFruits ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-green-500" />
                  </div>
                ) : collectedFruits.length === 0 ? (
                  <div className="text-center py-4 text-green-600 text-sm">
                    <p>No fruits collected yet!</p>
                    <p className="text-xs mt-1 opacity-75">Complete levels to collect fruits (99% drop rate)</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-4 gap-2">
                    {collectedFruits.map(fruit => (
                      <button
                        key={fruit.id}
                        onClick={() => handleExchangeFruit(fruit.id, fruit.quantity)}
                        disabled={isExchangingFruits}
                        className="flex flex-col items-center p-2 rounded-lg bg-white/80 hover:bg-white border border-green-200 hover:border-green-400 transition-all hover:scale-105"
                      >
                        <span className="text-2xl">{fruit.emoji}</span>
                        <span className="text-xs font-bold text-green-700">x{fruit.quantity}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Divider */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-gray-300"></div>
                <span className="text-xs text-gray-500 font-medium">or trade with players</span>
                <div className="flex-1 h-px bg-gray-300"></div>
              </div>
              
              {/* Grand Exchange */}
              <GrandExchange 
                userCoins={totalCoins} 
                onCoinsChange={onCoinsChange}
                userInventory={inventory}
                userEquipment={equipment}
                userCollectedFruits={collectedFruits.reduce((acc, f) => ({ ...acc, [f.id]: f.quantity }), {})}
              />
            </div>
          ) : activeTab === 'buy' ? (
            <div className="space-y-3">
              <p className="text-xs text-green-700 mb-3">
                Buy stars to get items and upgrades!
              </p>
              {STAR_PACKAGES.map((pkg) => (
                <div 
                  key={pkg.id}
                  className={`relative flex items-center gap-3 p-3 rounded-xl border-2 transition-all bg-white ${
                    pkg.popular 
                      ? 'border-green-400 ring-2 ring-green-200' 
                      : 'border-gray-200 hover:border-green-300'
                  }`}
                >
                  {pkg.popular && (
                    <div className="absolute -top-2 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-green-500 text-white text-xs font-bold rounded-full">
                      POPULAR
                    </div>
                  )}
                  <div className="w-14 h-14 bg-gradient-to-br from-yellow-100 to-amber-200 rounded-xl flex items-center justify-center">
                    <div className="text-center">
                      <Star className="w-6 h-6 fill-yellow-500 text-yellow-500 mx-auto" />
                      <span className="text-xs font-bold text-amber-800">{pkg.stars}</span>
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-display font-bold text-gray-900">{pkg.stars} Stars</h3>
                    <p className="text-sm text-gray-600">{pkg.display_price}</p>
                  </div>
                  <Button
                    onClick={() => handlePurchaseStars(pkg.id)}
                    disabled={purchasingPackage === pkg.id}
                    size="sm"
                    className="font-display font-bold bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600"
                    data-testid={`buy-stars-${pkg.id}`}
                  >
                    {purchasingPackage === pkg.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4 mr-1" />
                        Buy
                      </>
                    )}
                  </Button>
                </div>
              ))}
              <div className="mt-4 p-3 bg-blue-50 rounded-xl border border-blue-200">
                <p className="text-xs text-blue-700 flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Secure payment via Stripe with iDEAL or credit card
                </p>
              </div>
            </div>
          ) : activeTab === 'consumables' ? (
            <div className="space-y-3">
              <p className="text-xs text-amber-700 mb-3">
                Use these items during gameplay. You can buy them multiple times!
              </p>
              {CONSUMABLES.filter(item => item.cost > 0 && !item.droppable).map((item) => {
                const owned = inventory[item.id] || 0;
                const canAfford = totalStars >= item.cost;
                
                return (
                  <div 
                    key={item.id}
                    className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${
                      canAfford 
                        ? 'bg-white border-amber-200 hover:border-amber-400' 
                        : 'bg-gray-50 border-gray-200 opacity-60'
                    }`}
                  >
                    <div className="w-12 h-12 bg-gradient-to-br from-amber-100 to-orange-100 rounded-xl flex items-center justify-center text-2xl">
                      {item.emoji}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-display font-bold text-amber-900">{item.name}</h3>
                        {owned > 0 && (
                          <Badge className="bg-green-500 text-white text-xs">
                            {owned}x
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-amber-700">{item.description}</p>
                    </div>
                    <Button
                      onClick={() => handleBuy(item.id, false)}
                      disabled={!canAfford || buyingItem === item.id}
                      size="sm"
                      className={`font-display font-bold ${
                        canAfford 
                          ? 'bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600' 
                          : 'bg-gray-300'
                      }`}
                    >
                      <Star className="w-3 h-3 mr-1 fill-current" />
                      {item.cost}
                    </Button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-amber-700 mb-3">
                Permanent upgrades stay active forever after purchase!
              </p>
              {PERMANENT_UPGRADES.map((item) => {
                const owned = permanentUpgrades.includes(item.id);
                const canAfford = totalStars >= item.cost && !owned;
                
                return (
                  <div 
                    key={item.id}
                    className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${
                      owned 
                        ? 'bg-green-50 border-green-300'
                        : canAfford 
                        ? 'bg-white border-amber-200 hover:border-amber-400' 
                        : 'bg-gray-50 border-gray-200 opacity-60'
                    }`}
                  >
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-2xl ${
                      owned 
                        ? 'bg-gradient-to-br from-green-100 to-emerald-100' 
                        : 'bg-gradient-to-br from-purple-100 to-pink-100'
                    }`}>
                      {item.emoji}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-display font-bold text-amber-900">{item.name}</h3>
                        {owned && (
                          <Badge className="bg-green-500 text-white text-xs">
                            <Check className="w-3 h-3 mr-1" /> Active
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-amber-700">{item.description}</p>
                    </div>
                    {owned ? (
                      <div className="w-16 h-8 flex items-center justify-center">
                        <Sparkles className="w-5 h-5 text-green-500" />
                      </div>
                    ) : (
                      <Button
                        onClick={() => handleBuy(item.id, true)}
                        disabled={!canAfford || buyingItem === item.id}
                        size="sm"
                        className={`font-display font-bold ${
                          canAfford 
                            ? 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600' 
                            : 'bg-gray-300'
                        }`}
                      >
                        <Star className="w-3 h-3 mr-1 fill-current" />
                        {item.cost}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 bg-amber-100 border-t border-amber-200">
          <p className="text-center text-xs text-amber-700">
            💡 Earn stars by completing levels for the first time!
          </p>
        </div>
      </Card>
    </div>
  );
}

// Star display badge for header
export function StarBadge({ totalStars, onClick, compact = false }) {
  return (
    <button
      onClick={onClick}
      data-testid="shop-button"
      className={`flex items-center bg-gradient-to-r from-amber-400 to-yellow-400 hover:from-amber-500 hover:to-yellow-500 rounded-full shadow-md transition-all hover:scale-105 border border-amber-300 ${
        compact ? 'gap-0.5 px-1.5 py-0.5' : 'gap-1 px-2 py-1'
      }`}
    >
      <Star className={`fill-amber-800 text-amber-800 ${compact ? 'w-2.5 h-2.5' : 'w-3.5 h-3.5'}`} />
      <span className={`font-display font-bold text-amber-900 ${compact ? 'text-[10px]' : 'text-xs'}`}>{totalStars}</span>
    </button>
  );
}

// Coin Badge - displays coins earned from level completion (ultra compact for header)
export function CoinBadge({ totalCoins, compact = false }) {
  return (
    <div
      data-testid="coin-badge"
      className={`flex items-center bg-gradient-to-r from-yellow-500 to-orange-400 rounded-full shadow-md border border-yellow-600 ${
        compact ? 'gap-0.5 px-1.5 py-0.5' : 'gap-1 px-2 py-1'
      }`}
    >
      <span className={compact ? 'text-[10px]' : 'text-xs'}>🪙</span>
      <span className={`font-display font-bold text-yellow-900 ${compact ? 'text-[10px]' : 'text-xs'}`}>{totalCoins}</span>
    </div>
  );
}
